import { useMemo, useState } from 'react';
import { MenuItem, Category, Subcategory } from '@/types/common';
import MenuModal from './MenuModal';
import MenuSection from './MenuSection';
import { getCategoryIcon } from '@/utils/menuIcons';
import { createPortal } from 'react-dom';

interface MenuItemsProps {
  items: MenuItem[];
  categories: Category[];
  subcategories: Subcategory[];
  onItemClick: (item: MenuItem) => void;
  actionLabel?: string;
  showPrices?: boolean;
  onCreateItem?: (data: Omit<MenuItem, 'id' | 'created_at'>) => Promise<void>;
  onUpdateItem?: (id: string, data: Omit<MenuItem, 'id' | 'created_at'>) => Promise<void>;
  onDeleteItem?: (id: string) => Promise<void>;
  editable?: boolean;
}

export default function MenuItems({
  items,
  categories,
  subcategories,
  onItemClick,
  actionLabel = 'Select',
  showPrices = true,
  onCreateItem,
  onUpdateItem,
  onDeleteItem,
  editable = false
}: MenuItemsProps) {
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedSubcategory, setSelectedSubcategory] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [modalItem, setModalItem] = useState<MenuItem | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);

  // Available subcategories for selected category
  const availableSubcategories = useMemo(() => {
    if (selectedCategory === 'all') return [];
    return subcategories.filter(sub => sub.category_id === selectedCategory);
  }, [selectedCategory, subcategories]);

  // Filter items based on current filters
  const filteredItems = useMemo(() => {
    return items.filter(item => {
      // Category filter
      if (selectedCategory !== 'all' && item.category_id !== selectedCategory) {
        return false;
      }

      // Subcategory filter
      if (selectedSubcategory !== 'all' && item.subcategory_id !== selectedSubcategory) {
        return false;
      }

      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        return (
          item.name.toLowerCase().includes(query) ||
          item.description?.toLowerCase().includes(query)
        );
      }

      return true;
    });
  }, [items, selectedCategory, selectedSubcategory, searchQuery]);

  // Group filtered items by category
  const sections = useMemo(() => {
    // If filtering by category, only show that category
    const visibleCategories = selectedCategory === 'all'
      ? categories
      : categories.filter(c => c.id === selectedCategory);

    return visibleCategories
      .map(category => {
        const categoryItems = filteredItems.filter(item => 
          item.category_id === category.id
        );

        return {
          category,
          items: categoryItems
        };
      })
      .filter(section => section.items.length > 0)
      .sort((a, b) => a.category.order_index - b.category.order_index);
  }, [categories, filteredItems, selectedCategory]);

  // Reset subcategory when category changes
  const handleCategoryChange = (categoryId: string) => {
    setSelectedCategory(categoryId);
    setSelectedSubcategory('all');
  };

  return (
    <div className="flex flex-col">
      {/* Search and Filters */}
      <div className="bg-white/80 dark:bg-dark-bg/80 backdrop-blur-sm border-b border-gray-200 dark:border-dark-border">
        <div className="px-4 py-3 space-y-3">
          {/* Search Bar and Add Button */}
          <div className="flex gap-3">
            <div className="flex-1 relative">
              <input
                type="text"
                placeholder="Search menu items..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full h-10 pl-10 pr-4 rounded-lg border border-gray-300 dark:border-dark-border focus:ring-primary-500 focus:border-primary-500 dark:bg-dark-bg-light dark:text-dark-text-primary text-sm"
              />
              <i className="fas fa-search absolute left-3 top-3 text-gray-400" />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <i className="fas fa-times" />
                </button>
              )}
            </div>
            {editable && (
              <button
                onClick={() => setShowAddModal(true)}
                className="inline-flex items-center justify-center px-6 py-3 text-base font-semibold text-white rounded-lg transition-all duration-300 ease-out bg-gradient-to-r from-cyan-500 to-blue-500 shadow-[0_4px_0_0_#0369a1] hover:shadow-[0_6px_0_0_#0369a1] hover:-translate-y-[2px] active:translate-y-[2px] active:shadow-[0_2px_0_0_#0369a1]"
              >
                <i className="fas fa-plus mr-2" />
                Add Item
              </button>
            )}
          </div>

          {/* Category Filters */}
          <div className="flex overflow-x-auto -mb-3 pb-3 
            scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 
            scrollbar-track-transparent hover:scrollbar-thumb-gray-400 
            dark:hover:scrollbar-thumb-gray-500 
            scrollbar-thumb-rounded-full"
          >
            <div className="flex gap-2 px-1">
              <button
                onClick={() => handleCategoryChange('all')}
                className={`
                  px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap
                  transition-colors duration-200
                  ${selectedCategory === 'all'
                    ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400 shadow-sm'
                    : 'text-gray-600 dark:text-dark-text-secondary hover:bg-gray-50 dark:hover:bg-dark-bg-lighter'
                  }
                `}
              >
                <i className={`${getCategoryIcon('all')} mr-2`} />
                All Categories
              </button>
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => handleCategoryChange(category.id)}
                  className={`
                    px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap
                    transition-colors duration-200
                    ${selectedCategory === category.id
                      ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400 shadow-sm'
                      : 'text-gray-600 dark:text-dark-text-secondary hover:bg-gray-50 dark:hover:bg-dark-bg-lighter'
                    }
                  `}
                >
                  <i className={`${getCategoryIcon(category.name)} mr-2`} />
                  {category.name}
                </button>
              ))}
            </div>
          </div>

          {/* Subcategories */}
          {availableSubcategories.length > 0 && (
            <div className="px-4 py-2 -mx-4 border-t border-gray-200 dark:border-dark-border bg-gray-50 dark:bg-dark-bg-lighter">
              <div className="flex gap-2 overflow-x-auto 
                scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 
                scrollbar-track-transparent hover:scrollbar-thumb-gray-400 
                dark:hover:scrollbar-thumb-gray-500 
                scrollbar-thumb-rounded-full"
              >
                <button
                  onClick={() => setSelectedSubcategory('all')}
                  className={`
                    px-3 py-1.5 rounded-md text-sm font-medium whitespace-nowrap
                    transition-colors duration-200
                    ${selectedSubcategory === 'all'
                      ? 'bg-white dark:bg-dark-bg text-primary-600 dark:text-primary-400 shadow-sm'
                      : 'text-gray-600 dark:text-dark-text-secondary hover:bg-white dark:hover:bg-dark-bg'
                    }
                  `}
                >
                  All
                </button>
                {availableSubcategories.map((subcategory) => (
                  <button
                    key={subcategory.id}
                    onClick={() => setSelectedSubcategory(subcategory.id)}
                    className={`
                      px-3 py-1.5 rounded-md text-sm font-medium whitespace-nowrap
                      transition-colors duration-200
                      ${selectedSubcategory === subcategory.id
                        ? 'bg-white dark:bg-dark-bg text-primary-600 dark:text-primary-400 shadow-sm'
                        : 'text-gray-600 dark:text-dark-text-secondary hover:bg-white dark:hover:bg-dark-bg'
                      }
                    `}
                  >
                    {subcategory.name}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Menu Items */}
      <div className="flex-1 overflow-y-auto p-4">
        {filteredItems.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="bg-gray-100 dark:bg-dark-bg-lighter rounded-full p-4 mb-4">
              <i className="fas fa-search text-3xl text-gray-400" />
            </div>
            <h3 className="text-base font-medium text-gray-900 dark:text-dark-text-primary mb-1">
              No items found
            </h3>
            <p className="text-sm text-gray-500 dark:text-dark-text-secondary">
              {searchQuery
                ? 'Try adjusting your search or filters'
                : 'No items in this category yet'}
            </p>
          </div>
        ) : (
          <div className="space-y-8">
            {sections.map(({ category, items }) => (
              <MenuSection
                key={category.id}
                category={category}
                items={items}
                editable={editable}
                showPrices={showPrices}
                actionLabel={actionLabel}
                onItemClick={onItemClick}
                onUpdateItem={onUpdateItem}
                onEditItem={setModalItem}
                onDeleteItem={onDeleteItem}
              />
            ))}
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {modalItem && createPortal(
        <div className="fixed inset-0 z-[100]">
          <div className="absolute inset-0 bg-black/25 dark:bg-black/40" onClick={() => setModalItem(null)} />
          <div className="relative min-h-screen flex items-center justify-center p-4">
            <div className="relative w-full max-w-lg bg-white dark:bg-dark-bg rounded-lg shadow-xl">
              <MenuModal
                item={modalItem}
                categories={categories}
                subcategories={subcategories}
                onSubmit={async (data) => {
                  if (!onUpdateItem) return;
                  try {
                    await onUpdateItem(modalItem.id, data);
                    setModalItem(null);
                  } catch (error) {
                    console.error('Failed to update item:', error);
                  }
                }}
                onClose={() => setModalItem(null)}
              />
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Add Modal */}
      {showAddModal && createPortal(
        <div className="fixed inset-0 z-[100]">
          <div className="absolute inset-0 bg-black/25 dark:bg-black/40" onClick={() => setShowAddModal(false)} />
          <div className="relative min-h-screen flex items-center justify-center p-4">
            <div className="relative w-full max-w-lg bg-white dark:bg-dark-bg rounded-lg shadow-xl">
              <MenuModal
                categories={categories}
                subcategories={subcategories}
                onSubmit={async (data) => {
                  if (!onCreateItem) return;
                  try {
                    await onCreateItem(data);
                    setShowAddModal(false);
                  } catch (error) {
                    console.error('Failed to create item:', error);
                  }
                }}
                onClose={() => setShowAddModal(false)}
              />
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
